import { faExclamationTriangle } from "@fortawesome/pro-solid-svg-icons/faExclamationTriangle";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ErrorMessage } from "formik";
import { styled } from "@mui/material/styles";
import { kebabCase } from "@portal/app/_common/lib/changeCase";

interface FormFieldProps extends React.PropsWithChildren {
  label?: string | React.ReactNode;
  helpText?: string | React.ReactNode;
  // If True, a red star will be shown next to the label
  isRequired?: boolean;
  // If True, the text "Optional" will be shown next to the label
  isOptional?: boolean;
  // By default, the entire field is wrapped with a <label>. This causes unexpected behavior for checkboxes,
  // radios, and multi-field arrangements, so pass True here to use a regular <div> instead.
  withoutLabelWrap?: boolean;
  // If True, the text "Admin only" in red will be displayed next to the label
  isAdminOnly?: boolean;
  // If True, admin form grids place this field across every column.
  spanFullWidth?: boolean;
  styleToWrapper?: React.CSSProperties;
}

export const FormField = ({
  label,
  children,
  helpText,
  isRequired = false,
  isOptional = false,
  withoutLabelWrap = false,
  isAdminOnly = false,
  spanFullWidth = false,
  styleToWrapper,
}: FormFieldProps) => (
  <FormFieldWrapper
    style={styleToWrapper}
    as={withoutLabelWrap ? "div" : undefined}
    data-admin-form-span={spanFullWidth ? "full" : undefined}
  >
    {!!label && (
      <FormFieldLabel>
        {label} {isRequired && <FormFieldRequiredIcon>*</FormFieldRequiredIcon>}{" "}
        {isOptional && <FormFieldOptionalText>Optional</FormFieldOptionalText>}
        {isAdminOnly && <FormFieldAdminOnlyText>Admin only</FormFieldAdminOnlyText>}
      </FormFieldLabel>
    )}
    <FormFieldContent>{children}</FormFieldContent>
    {!!helpText && <FormFieldHelpText>{helpText}</FormFieldHelpText>}
  </FormFieldWrapper>
);

const FormFieldWrapper = styled("label")`
  display: block;
  margin: 1rem 0;

  &:first-child {
    margin-top: 0;
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

export const FormFieldLabel = styled("div")`
  padding-bottom: 0.3rem;
  font-size: 0.85rem;
`;

const FormFieldContent = styled("div")``;

export const FormFieldHelpText = styled("div")`
  margin-top: 0.5rem;
  font-size: 0.85rem;
  color: ${({ theme }) => theme.palette.dream.grayDark};
`;

const FormFieldRequiredIcon = styled("span")`
  color: ${({ theme }) => theme.palette.error.main};
`;

const FormFieldOptionalText = styled("span")`
  padding-left: 0.25em;
  color: ${({ theme }) => theme.palette.dream.grayMedium};
  font-size: 0.75em;
  text-transform: uppercase;
`;

const FormFieldAdminOnlyText = styled("span")`
  padding-left: 0.25em;
  color: ${({ theme }) => theme.palette.error.main};
  font-size: 0.75em;
  text-transform: uppercase;
`;

export const FormFieldError = ({ name }: { name: string }) => (
  <ErrorMessage name={name} data-testid={`${kebabCase(name)}-error`}>
    {(message) => (
      <FormFieldErrorWrapper data-error-scroll={name}>
        <FontAwesomeIcon icon={faExclamationTriangle} />
        {message}
      </FormFieldErrorWrapper>
    )}
  </ErrorMessage>
);

const FormFieldErrorWrapper = styled("div")`
  margin-top: 0.75rem;
  color: ${({ theme }) => theme.palette.error.main};
  font-size: 0.75rem;

  & > svg {
    font-size: 75%;
    vertical-align: 0;
    margin-right: 0.5rem;
  }

  & + & {
    margin-top: 0.25rem;
  }
`;
